import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type { CurrencyRateEnum } from '@/const/currency.const';
import { formatCurrencyRate } from '@/const/currency.const';
import { CountryCodeEnum } from '@/const/country.const';
import { CurrencyEnum } from '@/const/currency.const';
import { i18n } from '@/plugins/i18n';
import { safeClearStorage } from '@/utils/storage';
//封装storage方法

export const storageUtils = {
  setItem(key: string, value: string) {
    localStorage.setItem(key, value);
  },
  getItem(key: string) {
    return localStorage.getItem(key);
  },
  removeItem(key: string) {
    localStorage.removeItem(key);
  },
  clear() {
    safeClearStorage();
  }
};

//清空对象中的null或者undefined
export const clearNullOrUndefined = (obj: Record<string, unknown>) => {
  Object.keys(obj).forEach((key) => {
    if (obj[key] === null || obj[key] === undefined) {
      Reflect.deleteProperty(obj, key);
    }
  });

  return obj;
};

const TOKEN_KEY = 'access_token';

/**
   * 获取 token
   */
export const getToken = (): string => {
  return storageUtils.getItem(TOKEN_KEY) || '';
};

/**
   * 设置 token
   */
export const setToken = (token: string): void => {
  storageUtils.setItem(TOKEN_KEY, token);
};

/**
   * 移除 token
   */
export const removeToken = (): void => {
  storageUtils.removeItem(TOKEN_KEY);
};
/**
 * 格式化货币
 * @param value 数值
 * @param currency 货币类型
 * @returns 格式化后的字符串
 */
export const formatCurrency = (value: number, currency?: string): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }

  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);

  return currency ? `${currency} ${formatted}` : formatted;
};

/**
   * 格式化日期时间
   * @param timestamp 时间戳（毫秒或秒）
   * @param format 格式化字符串
   * @returns 格式化后的日期时间字符串
   */
export const formatDateTime = (timestamp: number, format = 'YYYY-MM-DD HH:mm:ss'): string => {
  if (!timestamp) {
    return '-';
  }

  // 判断是秒还是毫秒
  const ts = timestamp < 10000000000 ? timestamp * 1000 : timestamp;

  const date = new Date(ts);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return format
    .replace('YYYY', String(year))
    .replace('MM', month)
    .replace('DD', day)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds);
};

/**
   * 格式化百分比
   * @param value 数值（0-1之间的小数）
   * @returns 格式化后的百分比字符串
   */
export const formatPercent = (value: number): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }

  return `${(value * 100).toFixed(0)}%`;
};
export const formatNumberData = (
  num: number | string | undefined | null,
  original = false,
  maxDecimalPointNumber = 0,
  styleStr: 'decimal' | 'percent' = 'decimal',
  locale: string = navigator?.language ?? 'en-US'
) => {
  if (num === undefined || num === null || num === '') return num;

  const value = Number(num);

  if (isNaN(value)) return num;

  // 只格式化，不改变单位
  if (original) {
    return new Intl.NumberFormat(locale).format(value);
  }

  return new Intl.NumberFormat(locale, {
    style: styleStr,
    minimumFractionDigits: maxDecimalPointNumber,
    maximumFractionDigits: maxDecimalPointNumber
  }).format(value);
};

dayjs.extend(utc);

export function useTimeAt(timeData: number, formData: string) {
  if (timeData === 0) {
    return '-';
  }

  const storedOffset = window.localStorage.getItem('offset');
  let offset: number;

  const parsed = Number(storedOffset);

  if (storedOffset !== null && Number.isFinite(parsed) && parsed !== 0) {
    // 如果是 0，视为未初始化 + 默认 +08:00
    offset = parsed;
  } else {
    // 默认用的是 +08:00（480 分钟），这里直接对齐，避免本地浏览器时区不同或 offset=0 导致显示不一致
    offset = 8 * 60;
  }

  return dayjs.utc(timeData * 1000).utcOffset(offset).format(formData);
}
export function getLocalCountry() {
  const host = window.location.host;

  if (host.includes('betbus.co.za') || host.includes('mgt-dev-za.001thebest.com')) {
    return CountryCodeEnum.ZAF;
  }

  if (host.includes('betbus.mx') || host.includes('admin.betbus.com') || host.includes('bbbus-mgt-dev-www.001thebest.com')) {
    return CountryCodeEnum.MEX;
  }

  if (host.includes('admin.007bet.com.br') || host.includes('admin-6p5hdw.ai.bet.br') || host.includes('admin.ai.bet') || host.includes('dev-br.001thebest.com') || host.includes('br-staging-mgt.001thebest.com') || host.includes('ai-admin.work')) {
    return CountryCodeEnum.BRA;
  }

  return CountryCodeEnum.MEX;
}

export function getLocalCurrency() {
  const host = window.location.host;

  if (host.includes('betbus.co.za') || host.includes('mgt-dev-za.001thebest.com')) {
    return CurrencyEnum.ZAR;
  }

  if (host.includes('betbus.mx') || host.includes('admin.betbus.com') || host.includes('bbbus-mgt-dev-www.001thebest.com')) {
    return CurrencyEnum.MXN;
  }

  if (host.includes('admin.007bet.com.br') || host.includes('admin-6p5hdw.ai.bet.br') || host.includes('admin.ai.bet') || host.includes('dev-br.001thebest.com') || host.includes('br-staging-mgt.001thebest.com') || host.includes('ai-admin.work')) {
    return CurrencyEnum.BRL;
  }

  return CurrencyEnum.BRL;
}

//不需要转换汇率的数据展示货币
export function formCurrentCurrencyData(num: number, currency: string) {
  const currentCurrency = currency || getLocalCurrency();

  return formatCurrencyRate(num, currentCurrency as CurrencyRateEnum);
}

//复制
export const copyText = function (copyContent: string) {
  const url = copyContent;
  const input = document.createElement('input');
  const message = i18n.global.t('default.DefaultIndex.Common.CopySuccessfully');

  document.body.appendChild(input);
  input.setAttribute('value', url);
  input.select();
  document.execCommand('copy'); // 执行浏览器复制命令

  if (document.execCommand('copy')) {
    document.execCommand('copy');
  }

  document.body.removeChild(input);
  ElMessage.success(message);
};

// 获取时区偏移（+08:00 格式）
export const getTimezoneOffset = () => {
  const offset = -new Date().getTimezoneOffset();
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  const sign = offset >= 0 ? '+' : '-';

  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export function reactiveToRefObject<T extends Record<string, any>>(
  obj: T
): { [K in keyof T]: Ref<T[K]> } {
  return toRefs(obj) as { [K in keyof T]: Ref<T[K]> };
}
//下载
export const downloadFile = (data: any, type: string) => {
  const blob = new Blob([data], { type }); // 创建 blob 对象 type 可以使用 headers 里面的 contentType （对应文件类型）
  const a = document.createElement('a');
  const url = window.URL.createObjectURL(blob); // 创建媒体流 url ，详细了解可自己查 URL.createObjectURL（推荐 MDN ）

  a.href = url;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url); // 删除创建的媒体流 url 对象
};